/**
 * InstallPromptModal — "홈 화면에 앱으로 추가하세요" 안내 모달(모바일 브라우저, 로그인 직후 또는 내 계정 › 기기관리 [추가]).
 *  · ios     : 공유 → 홈 화면에 추가 → 추가 4단계 데모 + [알겠어요, 지금 추가할게요] / 나중에 / 다시 보지 않기
 *  · android : 데모 + [홈 화면에 추가](= beforeinstallprompt 시스템 설치창) / 나중에 / 다시 보지 않기
 * 닫기 사유는 부모가 저장한다(기기 단위 localStorage).
 */
import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/ui/Icon";
import { useBackdropDismiss } from "@/hooks/useBackdropDismiss";
import { useToast } from "@/hooks/useToast";
import type { A2hsPlatform } from "@/domain/a2hs";
import { getInstallPromptState, promptInstall, subscribeInstallPrompt } from "@/lib/installPrompt";
import { A2HS_APP_ICON, InstallDemoAndroid, InstallDemoIos } from "./A2hsDemo";
import "./a2hs.css";
import "./a2hs-demo.css";

export type InstallCloseReason = "later" | "never" | "ios_done" | "installed";

interface Props {
  platform: A2hsPlatform;
  onClose: (reason: InstallCloseReason) => void;
}

export function InstallPromptModal({ platform, onClose }: Props) {
  const toast = useToast();
  const dismiss = useBackdropDismiss(() => onClose("later"));
  const promptState = useSyncExternalStore(subscribeInstallPrompt, getInstallPromptState, getInstallPromptState);
  const isIos = platform === "ios";

  async function onAndroidInstall() {
    const r = await promptInstall();
    if (r === "accepted") {
      toast.success("홈 화면에 추가했습니다. 홈 화면의 JG ERP 로 열어 보세요.");
      onClose("installed");
    } else if (r === "dismissed") {
      toast.info("설치를 취소했습니다. 나중에 내 계정 › 기기관리에서 다시 할 수 있습니다.");
      onClose("later");
    } else {
      // 이벤트가 아직 안 왔거나(설치 조건 미충족) 이미 소모됨 — 브라우저 메뉴 안내
      toast.info("브라우저 메뉴(⋮) › ‘홈 화면에 추가’ 또는 ‘앱 설치’를 눌러 주세요.");
    }
  }

  return createPortal(
    <div className="modal-backdrop a2hs-backdrop" {...dismiss}>
      <div className="modal-panel a2hs-modal" role="dialog" aria-modal="true" aria-labelledby="a2hs-install-title">
        <div className="a2hs-hero">
          <img className="a2hs-hero-icon" src={A2HS_APP_ICON} alt="" />
          <h3 className="a2hs-title" id="a2hs-install-title">홈 화면에 앱으로 추가하세요</h3>
          <p className="a2hs-sub">
            주소창 없이 앱처럼 열리고, 알림도 받을 수 있어요
            <br />
            {isIos ? "아이폰은 Safari 에서 아래처럼 추가합니다" : "아래 버튼 한 번이면 끝납니다"}
          </p>
        </div>

        {isIos ? <InstallDemoIos /> : <InstallDemoAndroid />}

        {isIos && (
          <ol className="a2hs-steps is-ios">
            <li className="a2hs-step is-1"><span className="a2hs-step-no">1</span><span>주소창 왼쪽<br /><b>≡ → 공유</b></span></li>
            <li className="a2hs-step is-2"><span className="a2hs-step-no">2</span><span>목록에서<br /><b>홈 화면에 추가</b></span></li>
            <li className="a2hs-step is-3"><span className="a2hs-step-no">3</span><span>오른쪽 위<br /><b>추가</b> 누르기</span></li>
            <li className="a2hs-step is-4"><span className="a2hs-step-no">4</span><span>홈 화면의<br /><b>JG ERP</b> 로 열기</span></li>
          </ol>
        )}

        <div className="a2hs-actions">
          {isIos ? (
            <button type="button" className="btn-primary a2hs-cta" onClick={() => onClose("ios_done")}>
              알겠어요, 지금 추가할게요
            </button>
          ) : (
            <button type="button" className="btn-primary a2hs-cta" onClick={() => void onAndroidInstall()}>
              <Icon name="plus" size={18} /> 홈 화면에 추가
            </button>
          )}
          <div className="a2hs-actions-row">
            <button type="button" className="btn-ghost is-soft" onClick={() => onClose("later")}>나중에</button>
            <button type="button" className="btn-ghost is-quiet" onClick={() => onClose("never")}>다시 보지 않기</button>
          </div>
          <p className="a2hs-foot">
            {isIos
              ? "추가한 뒤에는 홈 화면의 JG ERP 로 열어 주세요 · 나중에 내 계정 › 기기관리에서 다시 볼 수 있습니다"
              : promptState.available
                ? "누르면 휴대폰의 설치 확인창이 뜹니다 · 나중에 내 계정 › 기기관리에서도 가능"
                : "설치창이 안 뜨면 브라우저 메뉴(⋮) › ‘홈 화면에 추가’를 눌러 주세요"}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
